// Shared document uploader.
//
// Drop into any form that has a *_document_id field. POSTs the file as
// multipart to /api/bead/documents and calls onUploaded(documentId).
// Supports drag-drop OR click-to-pick. Shows progress + filename + sha
// once uploaded; offers a remove (×) action that clears the parent's
// reference (parent decides whether to also DELETE the doc).
//
// Usage:
//   <DocumentUploader
//     value={form.igce_document_id}
//     onChange={(id) => setForm({ ...form, igce_document_id: id })}
//     vendorId={vendorId}     // optional metadata fields stored alongside
//     projectId={projectId}
//     docType="igce"
//     accept=".pdf,.doc,.docx,.png,.jpg,image/*"
//     label="IGE document"
//   />

import { useRef, useState } from 'react';

type Props = {
  value: string | null;
  onChange: (documentId: string | null) => void;
  vendorId?: string;
  projectId?: string;
  awardId?: string;
  milestoneId?: string;
  docType?: string;
  accept?: string;
  label?: string;
  hint?: string;
};

type DocMeta = {
  id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number;
  sha256: string;
};

const fmtBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export default function DocumentUploader({
  value, onChange,
  vendorId, projectId, awardId, milestoneId, docType,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,image/*',
  label, hint,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<DocMeta | null>(null);
  const [hovering, setHovering] = useState(false);

  // If value is set but we don't have meta (e.g., page loaded with a
  // pre-existing document_id), fetch metadata once.
  if (value && !meta && !busy) {
    fetch(`/api/bead/documents/${value}/meta`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then((m: DocMeta | null) => { if (m) setMeta(m); })
      .catch(() => {});
  }

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (vendorId)    fd.append('vendor_id', vendorId);
      if (projectId)   fd.append('project_id', projectId);
      if (awardId)     fd.append('award_id', awardId);
      if (milestoneId) fd.append('milestone_id', milestoneId);
      if (docType)     fd.append('doc_type', docType);
      const res = await fetch('/api/bead/documents', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `upload failed (${res.status})`);
      }
      const m: DocMeta = await res.json();
      setMeta(m);
      onChange(m.id);
    } catch (e: any) {
      setError(e.message || 'upload failed');
    } finally {
      setBusy(false);
    }
  }

  function clear() {
    setMeta(null);
    setError(null);
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div>
      {label && <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, marginBottom: 4 }}>{label}</div>}
      {meta ? (
        <div style={{
          background: '#f0fdf4', border: '1px solid #86efac',
          borderRadius: 4, padding: '8px 10px',
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
        }}>
          <span style={{ fontSize: 14 }}>📎</span>
          <a
            href={`/api/bead/documents/${meta.id}`}
            target="_blank" rel="noreferrer"
            style={{ color: '#0f172a', fontWeight: 500, textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={`${meta.filename} · ${fmtBytes(meta.size_bytes)} · sha256:${meta.sha256.slice(0, 12)}…`}
          >{meta.filename}</a>
          <span style={{ color: '#64748b', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{fmtBytes(meta.size_bytes)}</span>
          <button
            onClick={clear} type="button"
            style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1 }}
            title="Remove"
          >×</button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setHovering(true); }}
          onDragLeave={() => setHovering(false)}
          onDrop={(e) => {
            e.preventDefault();
            setHovering(false);
            const f = e.dataTransfer.files?.[0];
            if (f) upload(f);
          }}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `1px dashed ${error ? '#ef4444' : hovering ? '#3b82f6' : '#cbd5e1'}`,
            background: hovering ? '#eff6ff' : busy ? '#f1f5f9' : '#fff',
            borderRadius: 4, padding: '10px 12px',
            cursor: busy ? 'wait' : 'pointer',
            fontSize: 12, color: error ? '#991b1b' : '#64748b',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          }}
        >
          <span>
            {busy ? 'Uploading…' : error ? `⚠ ${error}` : '📎 Drop file or click to upload'}
          </span>
          <span style={{ fontSize: 10, color: '#94a3b8' }}>{accept.replace(/image\/\*,?/, '').replace(/^,/, '')}</span>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
            style={{ display: 'none' }}
          />
        </div>
      )}
      {hint && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

// Read-only link (for displaying an already-attached doc without an
// upload affordance). Useful in tables.
export function DocumentLink({ documentId, label }: { documentId: string | null; label?: string }) {
  if (!documentId) return <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>;
  return (
    <a
      href={`/api/bead/documents/${documentId}`}
      target="_blank" rel="noreferrer"
      style={{ color: '#3b82f6', fontSize: 12, textDecoration: 'none' }}
    >📎 {label ?? 'View'}</a>
  );
}
